import { SyntheticEvent } from 'react';
import { useFormContext } from 'react-hook-form';
import { CommonKeys } from '@components/EntityDrawers/constants/keys';
import { ISelectOption } from '@constants/entities/ui';
import { STANDARD_REF_UPDATED_IN_INSIGHTS } from '@constants/localStorage';
import { useProject } from '@context/Project';
import { useToast } from '@context/Toast';
import useProjectId from '@hooks/useProjectId';
import useReferenceMaps from '@hooks/useReferenceMaps';
import useUser from '@hooks/useUser';
import { useChangeNodeReferencesMutation } from '@store/services/nodes';
import { NodeReferencesActions } from '@store/services/nodes/types';
import { parseErrorResponse } from '@utils/helpers';
import { Tabs } from '@views/Insights/types';

const useChangeReference = () => {
  const projectId = useProjectId();
  const { showToast } = useToast();
  const { isInsightsMode, insightsTab } = useProject();
  const { watch, setValue } = useFormContext();
  const [changeNodeReference, { isLoading: isChangingReference }] =
    useChangeNodeReferencesMutation();

  const [{ qnousMap, nistMap, isoMap, ismMap, cisMap }] = useReferenceMaps();
  const [{ isQmsUser }] = useUser();

  const handleChangeReference =
    (
      type:
        | CommonKeys.QnousRef
        | CommonKeys.NistRef
        | CommonKeys.IsoRef
        | CommonKeys.IsmRef
        | CommonKeys.CisRef,
    ) =>
    async (_: SyntheticEvent, newReferences: ISelectOption[]) => {
      const currentReferences = watch(type);

      const revertChanges = () => {
        setValue(type, currentReferences);
      };

      try {
        setValue(type, newReferences);

        const addedReference = newReferences.find(
          (newReference) =>
            !currentReferences.some(
              (currentReference: ISelectOption) =>
                currentReference.value === newReference.value,
            ),
        );

        const removedReference = addedReference
          ? null
          : currentReferences.find(
              (currentReference: ISelectOption) =>
                !newReferences.some(
                  (newReference) =>
                    currentReference.value === newReference.value,
                ),
            );

        const { nist, qnous_ref, iso, ism, cis } = await changeNodeReference({
          qnous_ref:
            watch(CommonKeys.QnousRef).map(
              (ref: ISelectOption) => ref.value as string,
            ) ?? [],

          nist:
            watch(CommonKeys.NistRef).map(
              (ref: ISelectOption) => ref.value as string,
            ) ?? [],

          iso:
            watch(CommonKeys.IsoRef).map(
              (ref: ISelectOption) => ref.value as string,
            ) ?? [],

          ism:
            watch(CommonKeys.IsmRef).map(
              (ref: ISelectOption) => ref.value as string,
            ) ?? [],

          cis:
            watch(CommonKeys.CisRef).map(
              (ref: ISelectOption) => ref.value as string,
            ) ?? [],

          type,

          project_id: projectId,

          ...(addedReference
            ? {
                action: NodeReferencesActions.Add,
                value: addedReference.value as string,
              }
            : {
                action: NodeReferencesActions.Rem,
                value: (removedReference?.value as string) ?? '',
              }),
        }).unwrap();

        if (isQmsUser && qnous_ref) {
          setValue(
            CommonKeys.QnousRef,
            qnous_ref.map((ref) => ({
              value: ref,
              label: qnousMap.get(ref) ?? ref,
            })),
            { shouldDirty: true },
          );
        }

        setValue(
          CommonKeys.NistRef,
          nist.map((ref) => ({ value: ref, label: nistMap.get(ref) ?? ref })),
          { shouldDirty: true },
        );

        setValue(
          CommonKeys.IsoRef,
          iso.map((ref) => ({ value: ref, label: isoMap.get(ref) ?? ref })),
          { shouldDirty: true },
        );

        setValue(
          CommonKeys.IsmRef,
          ism.map((ref) => ({ value: ref, label: ismMap.get(ref) ?? ref })),
          { shouldDirty: true },
        );

        setValue(
          CommonKeys.CisRef,
          cis.map((ref) => ({ value: ref, label: cisMap.get(ref) ?? ref })),
          { shouldDirty: true },
        );

        const isIndustryStandard = type !== CommonKeys.QnousRef;

        if (
          isInsightsMode &&
          insightsTab === Tabs.Completeness &&
          isIndustryStandard
        ) {
          localStorage.setItem(STANDARD_REF_UPDATED_IN_INSIGHTS, 'true');
        }
      } catch (error) {
        revertChanges();
        showToast(parseErrorResponse(error), 'error');
      }
    };

  return { handleChangeReference, isChangingReference };
};

export default useChangeReference;
